import './App.css'
import Pages from "./pages/index.jsx"
import { Toaster } from "@/components/ui/toaster"
import CookieNotice from "@/components/CookieNotice"
import { AuthProvider } from "@/contexts/AuthContext"
import { ThemeProvider } from "@/contexts/ThemeContext"
import { initSentry, withSentryErrorBoundary } from "@/utils/sentry"
import { initializeAnalytics } from "@/utils/analytics"
import { HelmetProvider } from 'react-helmet-async'
import { useEffect } from 'react'

// Initialize Sentry
initSentry();

// Initialize Google Analytics 4
// Only runs in staging/production environments
initializeAnalytics();

function App() {
  // Prevent horizontal swipe gestures (Safari swipe-to-go-back, trackpad swipes)
  useEffect(() => {
    const handleWheel = (e) => {
      // Only prevent horizontal scrolling at the document level when it's clearly a navigation gesture
      // Allow horizontal scrolling within scrollable containers
      const target = e.target;
      const isScrollable = target.scrollWidth > target.clientWidth || 
                          target.closest('[style*="overflow-x"], [class*="overflow-x"], [class*="scroll"]');
      
      // If the element or its parent is scrollable, allow the scroll
      if (isScrollable) {
        return;
      }

      // Prevent horizontal scrolling with trackpad/mouse wheel when not in a scrollable container
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 0) {
        e.preventDefault();
      }
    };

    // Add event listener for wheel events (trackpad gestures)
    document.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <ThemeProvider>
          <Pages />
          <Toaster />
          <CookieNotice />
        </ThemeProvider>
      </AuthProvider>
    </HelmetProvider>
  )
}

// Create the fallback component
const ErrorFallback = ({ error, resetError }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100">
    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
      <div className="text-center">
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 rounded-full mb-6">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          Application Error
        </h1>
        
        <p className="text-gray-600 mb-6">
          We encountered an unexpected error. This has been reported to our team.
        </p>

        <div className="space-y-3">
          <button
            onClick={resetError}
            className="w-full bg-brand-600 text-white px-6 py-3 rounded-md font-medium hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Try Again
          </button>
          
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-gray-600 text-white px-6 py-3 rounded-md font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Wrap App with Sentry error boundary
export default withSentryErrorBoundary(App, ErrorFallback)